import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Banknote, CheckCircle2, Store, Users } from "lucide-react";
import { StatCard } from "@/components/common/StatCard";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { salesApi, shopApi, tenantsApi, usersApi } from "@/lib/api";
import { businessTypeLabel, isShop } from "@/lib/api/types";

/** Today, the same two numbers for a restaurant (orders) and a shop (sales). */
interface DayFigures {
  todayGross: number;
  orderCount: number;
}
import { formatMoney, formatNumber } from "@/lib/utils";

export default function Dashboard() {
  const tenants = useQuery({ queryKey: ["tenants"], queryFn: tenantsApi.list });
  const users = useQuery({ queryKey: ["users", "all"], queryFn: () => usersApi.list() });

  // One today-sales call per business, in parallel — restaurants from
  // order-service, shops from the shop layer, folded into one shape.
  const salesQueries = useQueries({
    queries: (tenants.data ?? []).map((tenant) => ({
      queryKey: ["sales", "today", tenant.id, tenant.businessType],
      queryFn: async (): Promise<DayFigures> => {
        if (isShop(tenant.businessType)) {
          const s = await shopApi.summary(tenant.id);
          return { todayGross: s.todayTotal, orderCount: s.todayCount };
        }
        const s = await salesApi.today(tenant.id);
        return { todayGross: s.todayGross, orderCount: s.orderCount };
      },
    })),
  });

  const salesByTenant = useMemo(() => {
    const map = new Map<number, DayFigures>();
    (tenants.data ?? []).forEach((tenant, i) => {
      const data = salesQueries[i]?.data;
      if (data) map.set(tenant.id, data);
    });
    return map;
  }, [tenants.data, salesQueries]);

  const salesLoading = salesQueries.some((q) => q.isLoading);
  const platformGross = salesQueries.reduce((sum, q) => sum + (q.data?.todayGross ?? 0), 0);
  const activeCount = (tenants.data ?? []).filter((t) => t.active).length;

  const rows = useMemo(
    () => (tenants.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [tenants.data],
  );

  return (
    <div>
      <PageHeader title="Dashboard" description="Platform overview across all businesses" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total businesses"
          value={tenants.data ? formatNumber(tenants.data.length) : "…"}
          icon={Store}
        />
        <StatCard
          label="Active businesses"
          value={tenants.data ? formatNumber(activeCount) : "…"}
          icon={CheckCircle2}
          tone="success"
        />
        <StatCard
          label="Total users"
          value={users.data ? formatNumber(users.data.length) : "…"}
          icon={Users}
        />
        <StatCard
          label="Today's platform gross"
          value={tenants.data && !salesLoading ? formatMoney(platformGross) : "…"}
          icon={Banknote}
          tone="success"
        />
      </div>

      <Card className="mt-6">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>Businesses today</CardTitle>
            <CardDescription>Today's sales per business</CardDescription>
          </div>
          <Link to="/tenants" className="text-sm font-medium text-primary hover:underline">
            Manage businesses
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {tenants.isLoading && <Skeleton className="m-5 h-48" />}
          {tenants.data && rows.length === 0 && (
            <EmptyState
              icon={Store}
              title="No businesses yet"
              description="Onboard your first business from the Businesses page."
            />
          )}
          {rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead className="hidden sm:table-cell">Type</TableHead>
                  <TableHead className="text-right">Today's sales</TableHead>
                  <TableHead className="hidden text-right md:table-cell">Sales / orders</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((tenant) => {
                  const sales = salesByTenant.get(tenant.id);
                  return (
                    <TableRow key={tenant.id}>
                      <TableCell>
                        <Link
                          to={`/tenants/${tenant.id}`}
                          className="font-medium text-foreground hover:text-primary hover:underline"
                        >
                          {tenant.name}
                        </Link>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant="outline">{businessTypeLabel(tenant.businessType)}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {sales ? formatMoney(sales.todayGross) : "…"}
                      </TableCell>
                      <TableCell className="hidden text-right tabular-nums md:table-cell">
                        {sales ? formatNumber(sales.orderCount) : "…"}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant={tenant.active ? "success" : "destructive"}>
                          {tenant.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
